import { Component, Input, OnInit, OnDestroy, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import SearchItem from '@Interfaces/SearchItem';
import { SearchActions } from '@app/core/ngrx/actions';
import { State } from '@app/core/ngrx/reducers';
import { ToolService } from '@app/core/services/tool/tool.service';
import { ProgramService } from '@app/core/services/program/program.service';
import { ModalController } from '@ionic/angular';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { Angulartics2GoogleTagManager } from 'angulartics2';
import { IonicModule } from '@ionic/angular';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons';
import { FilterCategoryModalComponent } from './filter-category-modal/filter-category-modal.component';

@Component({
    selector: 'app-search-program',
    templateUrl: './search-program.component.html',
    styleUrls: ['./search-program.component.scss'],
    standalone: true,
    imports: [CommonModule, FormsModule, ReactiveFormsModule, IonicModule, FontAwesomeModule],
    schemas: [CUSTOM_ELEMENTS_SCHEMA]})
export class SearchProgramMobileComponent implements OnInit, OnDestroy {
    @Input() content: any;
    @Input() ordering: string;
    @Input() totalProgramCount: number | undefined;

    /** Updated when parent runs search (Apply / removeSelection); used for button text */
    programCount: number | undefined;
    private programCountSub?: Subscription;

    faChevronDown = faChevronDown;

    selected: any = {
        specialty: '',
        order: 'date-desc',
        tag: {
            value: '',
        },
        type: {
            value: '',
        },
        states: {
            value: '',
        },
        cities: {
            value: '',
        },
        date: {
            value: '',
        },
    };

    selectedSpecialtyValue: SearchItem[] = [];
    selectedTypeValue: SearchItem[] = [];
    selectedTagValue: SearchItem[] = [];
    selectedStateValue: SearchItem[] = [];
    selectedCityValue: SearchItem[] = [];
    selectedDateValue: SearchItem[] = [];

    constructor(
        public toolService: ToolService,
        private modalCtrl: ModalController,
        private programService: ProgramService,
        private store: Store<State>,
        private angulartics2GoogleTagManager: Angulartics2GoogleTagManager
    ) {}

    ngOnInit() {
        if (this.content) {
            try {
                this.selected.specialty = [...this.content.content.specialty].filter((s: SearchItem) => s.checked === true) || '';
                this.selectedSpecialtyValue = [...this.selected.specialty];
                //
                this.selected.tags = [...this.content.content.tags].filter((s: SearchItem) => s.checked === true) || '';
                this.selectedTagValue = [...this.selected.tags];
                //
                this.selected.types = [...this.content.content.types].filter((s: SearchItem) => s.checked === true) || '';
                this.selectedTypeValue = [...this.selected.types];
                //
                this.selected.states = [...this.content.content.states].filter((s: SearchItem) => s.checked === true) || '';
                this.selectedStateValue = [...this.selected.states];
                //
                this.selected.cities = [...this.content.content.cities].filter((s: SearchItem) => s.checked === true) || '';
                this.selectedCityValue = [...this.selected.cities];
                //
                this.selected.dates = [...this.content.content.dates].filter((s: SearchItem) => s.checked === true) || '';
                this.selectedDateValue = [...this.selected.dates];
                //
                this.selected.order = this.ordering || 'date-desc';
            } catch (error) {
                console.log(error);
            }
        }
        this.programCount = this.totalProgramCount;
        this.programCountSub = this.toolService.getSearchProgramCount().subscribe((count) => {
            this.programCount = count ?? undefined;
        });
    }

    ngOnDestroy(): void {
        this.programCountSub?.unsubscribe();
    }

    get searchButtonText(): string {
        const n = this.programCount;
        if (n === null || n === undefined) return 'Show results';
        if (n === 0) return 'No results';
        if (n > 50) return 'Show 50+ results';
        return `Show ${n} results`;
    }

    get hasNoResults(): boolean {
        return this.programCount === 0;
    }

    private getCurrentFiltersData(): any {
        const specialties = [...this.selected.specialty].map((spec: SearchItem) => ({ ...spec, checked: true }));
        const types = [...this.selected.types].map((val: any) => ({ ...val, checked: true }));
        const tags = [...this.selected.tags].map((val: any) => ({ ...val, checked: true }));
        const cities = [...this.selected.cities].map((val: any) => ({ ...val, checked: true }));
        const states = [...this.selected.states].map((val: any) => ({ ...val, checked: true }));
        const dates = [...this.selected.dates].map((val: any) => ({ ...val, checked: true }));
        return {
            specialty: specialties,
            types,
            tags,
            cities,
            states,
            dates,
            order: this.selected.order,
        };
    }

    trackByIndex = (index: number): number => index;
    trackBySelection = (_index: number, sel: { category: string; label: string; item: any }): string =>
        `${sel.category}-${sel.item?.value ?? sel.item?.display ?? _index}`;

    get selectionsList(): { category: string; label: string; item: any }[] {
        const list: { category: string; label: string; item: any }[] = [];
        const add = (category: string, arr: any[]) => {
            (arr || []).forEach((it) =>
                list.push({
                    category,
                    label: it?.display ?? it?.value ?? '',
                    item: it,
                })
            );
        };
        add('specialty', this.selectedSpecialtyValue);
        add('type', this.selectedTypeValue);
        add('dates', this.selectedDateValue);
        add('tag', this.selectedTagValue);
        add('states', this.selectedStateValue);
        add('cities', this.selectedCityValue);
        return list;
    }

    private resetQuickActionCategoryContext(): void {
        this.programService.setResetCatagoriesSubject.next(true);
        this.store.dispatch(
            SearchActions.setSearchContext({
                context: 'Search for your next clinical experience with us.',
            })
        );
    }

    removeSelection(category: string, item: any): void {
        const key = item?.value ?? item?.display;
        const filterOut = (arr: any[]) => (arr || []).filter((x) => (x?.value ?? x?.display) !== key);
        switch (category) {
            case 'specialty':
                this.selectedSpecialtyValue = filterOut(this.selectedSpecialtyValue);
                this.selected.specialty = this.selectedSpecialtyValue;
                break;
            case 'type':
                this.selectedTypeValue = filterOut(this.selectedTypeValue);
                this.selected.types = this.selectedTypeValue;
                break;
            case 'dates':
                this.selectedDateValue = filterOut(this.selectedDateValue);
                this.selected.dates = this.selectedDateValue;
                break;
            case 'tag':
                this.selectedTagValue = filterOut(this.selectedTagValue);
                this.selected.tags = this.selectedTagValue;
                break;
            case 'states':
                this.selectedStateValue = filterOut(this.selectedStateValue);
                this.selected.states = this.selectedStateValue;
                break;
            case 'cities':
                this.selectedCityValue = filterOut(this.selectedCityValue);
                this.selected.cities = this.selectedCityValue;
                break;
        }
        this.resetQuickActionCategoryContext();
        this.toolService.shareFilters.next(this.getCurrentFiltersData());
    }

    async openSelect(_ev: UIEvent, category: string): Promise<void> {
        const list = this.getListCopyForCategory(category);
        if (!list) return;
        const modal = await this.modalCtrl.create({
            component: FilterCategoryModalComponent,
            componentProps: { list, title: this.getCategoryModalTitle(category) },
            cssClass: 'filter-category-modal',
        });
        await modal.present();
        const { data } = await modal.onDidDismiss();
        if (data?.list) {
            this.applyModalResult(category, data.list);
            this.resetQuickActionCategoryContext();
            this.toolService.shareFilters.next(this.getCurrentFiltersData());
        }
    }

    private getCategoryModalTitle(category: string): string {
        const titles: Record<string, string> = {
            specialty: 'Medical Specialty',
            type: 'Rotation Type',
            dates: 'Months',
            tag: 'Tags',
            states: 'States',
            cities: 'Cities',
        };
        return titles[category] ?? category;
    }

    private getListCopyForCategory(category: string): any[] | null {
        const raw = this.content?.content;
        if (!raw) return null;
        let arr: any[];
        let selectedValues: any[];
        switch (category) {
            case 'specialty': arr = raw.specialty; selectedValues = this.selectedSpecialtyValue; break;
            case 'type': arr = raw.types; selectedValues = this.selectedTypeValue; break;
            case 'dates': arr = raw.dates; selectedValues = this.selectedDateValue; break;
            case 'tag': arr = raw.tags; selectedValues = this.selectedTagValue; break;
            case 'states': arr = raw.states; selectedValues = this.selectedStateValue; break;
            case 'cities': arr = raw.cities; selectedValues = this.selectedCityValue; break;
            default: return null;
        }
        if (!arr) return null;
        const selectedSet = new Set(selectedValues?.map((s: any) => s.value ?? s.display) ?? []);
        // On first open, selectedValues may not be synced yet – use content's checked state as fallback
        if (selectedSet.size === 0 && arr.length) {
            arr.forEach((i: any) => {
                if (i?.checked) selectedSet.add(i?.value ?? i?.display);
            });
        }
        return arr.map((i: any) => ({ ...i, checked: selectedSet.has(i.value ?? i.display) }));
    }

    private applyModalResult(category: string, list: any[]): void {
        const checked = (list || []).filter((i: any) => i.checked);
        const value = checked.length ? [...checked] : [];

        switch (category) {
            case 'specialty':
                this.selected.specialty = value;
                this.selectedSpecialtyValue = value;
                break;
            case 'type':
                this.selected.types = value;
                this.selectedTypeValue = value;
                break;
            case 'dates':
                this.selected.dates = value;
                this.selectedDateValue = value;
                break;
            case 'tag':
                this.selected.tags = value;
                this.selectedTagValue = value;
                break;
            case 'states':
                this.selected.states = value;
                this.selectedStateValue = value;
                break;
            case 'cities':
                this.selected.cities = value;
                this.selectedCityValue = value;
                break;
            default:
                break;
        }
        // Keep order in sync with parent and search-filters (e.g. desktop Sort By dropdown)
        if (this.selected.order) {
            this.toolService.searchSortValue.next(this.selected.order);
        }
    }

    dismiss(): void {
        this.angulartics2GoogleTagManager.eventTrack('Filter Menu Closed', {
            category: 'Search',
        });

        this.modalCtrl.dismiss();
    }

    setDate(ev: any): void {
        this.angulartics2GoogleTagManager.eventTrack('Filter Dates', {
            category: 'Search',
        });

        if (ev.detail) {
            if (ev.detail.value.length === 0) {
                this.selected.dates = [...this.selected.dates].map((el: SearchItem) => {
                    return {
                        ...el,
                        checked: false,
                    };
                });
            } else {
                ev.detail.value.forEach((selected: any) => {
                    this.selected.dates = [...ev.detail.value].map((el: SearchItem) => ({ ...el, checked: true }));
                });
            }
        }
    }

    setTag(ev: any): void {
        this.angulartics2GoogleTagManager.eventTrack('Filter Tags', {
            category: 'Search',
        });

        if (ev.detail) {
            if (ev.detail.value.length === 0) {
                this.selected.tags = [...this.selected.tags].map((el: SearchItem) => {
                    return {
                        ...el,
                        checked: false,
                    };
                });
            } else {
                ev.detail.value.forEach((selected: any) => {
                    this.selected.tags = [...ev.detail.value].map((el: SearchItem) => ({ ...el, checked: true }));
                });
            }
        }
    }

    setType(ev: any): void {
        this.angulartics2GoogleTagManager.eventTrack('Filter Types', {
            category: 'Search',
        });
        if (ev.detail) {
            if (ev.detail.value.length === 0) {
                this.selected.types = [...this.selected.types].map((el: SearchItem) => {
                    return {
                        ...el,
                        checked: false,
                    };
                });
            } else {
                ev.detail.value.forEach((selected: any) => {
                    this.selected.types = [...ev.detail.value].map((el: SearchItem) => ({ ...el, checked: true }));
                });
            }
        }
    }

    setSpecialty(ev: any): void {
        this.angulartics2GoogleTagManager.eventTrack('Filter Specialty', {
            category: 'Search',
        });

        if (ev.detail) {
            if (ev.detail.value.length === 0) {
                this.selected.specialty = [...this.selected.specialty].map((el: SearchItem) => {
                    return {
                        ...el,
                        checked: false,
                    };
                });
            } else {
                ev.detail.value.forEach((selected: any) => {
                    this.selected.specialty = [...ev.detail.value].map((el: SearchItem) => ({ ...el, checked: true }));
                });
            }
        }
    }

    setState(ev: any): void {
        this.angulartics2GoogleTagManager.eventTrack('Filter States', {
            category: 'Search',
        });

        if (ev.detail) {
            if (ev.detail.value.length === 0) {
                this.selected.states = [...this.selected.states].map((el: SearchItem) => {
                    return {
                        ...el,
                        checked: false,
                    };
                });
            } else {
                ev.detail.value.forEach((selected: any) => {
                    this.selected.states = [...ev.detail.value].map((el: SearchItem) => ({ ...el, checked: true }));
                });
            }
        }
    }

    setCity(ev: any): void {
        this.angulartics2GoogleTagManager.eventTrack('Filter Cities', {
            category: 'Search',
        });

        if (ev.detail) {
            if (ev.detail.value.length === 0) {
                this.selected.cities = [...this.selected.cities].map((el: SearchItem) => {
                    return {
                        ...el,
                        checked: false,
                    };
                });
            } else {
                ev.detail.value.forEach((selected: any) => {
                    this.selected.cities = [...ev.detail.value].map((el: SearchItem) => ({ ...el, checked: true }));
                });
            }
        }
    }

    clearRow(type: string): void {
        switch (type) {
            case 'specialty':
                this.selected.specialty = [];
                this.selectedSpecialtyValue = [];

                break;
            case 'type':
                this.selected.types = [];
                this.selectedTypeValue = [];
                break;
            case 'dates':
                this.selected.dates = [];
                this.selectedDateValue = [];
                break;
            case 'tag':
                this.selected.tags = [];
                this.selectedTagValue = [];
                break;
            case 'states':
                this.selected.states = [];
                this.selectedStateValue = [];
                break;
            case 'cities':
                this.selected.cities = [];
                this.selectedCityValue = [];
                break;
            default:
                break;
        }

        for (const k in this.selected) {
            if (k == type) {
                this.selected[k] = '';
            }
        }
    }

    reset(): void {
        this.angulartics2GoogleTagManager.eventTrack('Filter Menu Reset Filters Clicked', { category: 'Search' });

        this.store.dispatch(SearchActions.setSearchLoading({ loading: true }));

        this.resetQuickActionCategoryContext();

        this.store.dispatch(
            SearchActions.setSearchContext({
                context: 'Search for your next clinical experience with us.',
            })
        );

        for (const k in this.selected) {
            if (this.selected[k]) {
                this.selected[k] = '';
            }
        }

        // hack to reset all filters
        const data = {
            specialty: [],
            types: [],
            tags: [],
            cities: [],
            states: [],
            locations: [],
            dates: [],
        };
        this.modalCtrl.dismiss(data);
    }

    /** Close modal and show results; do not send request again (search already run on Apply / removeSelection) */
    runSearch() {
        this.modalCtrl.dismiss();
    }
}
